"use client"

import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

import { HeroHeader } from "./header"
import { Sparkle, ArrowUpRight, CirclePlay } from 'lucide-react'
import { useTranslations } from "next-intl"
import { Globe } from "@/components/ui/globe"
import { SpinningText } from "@/components/ui/spinning-text"
import { WordRotate } from "@/components/ui/word-rotate"
import { StripedPattern } from "@/components/ui/striped-pattern"
import { BorderBeam } from "@/components/ui/border-beam"
import { AnimatedGradientText } from "@/components/ui/animated-gradient-text"
import { ChevronRight } from 'lucide-react'
import { cn } from "@/lib/utils"
import { getAuthUrl } from "@/lib/auth-urls"
import { useLanguage } from "@/lib/LanguageContext"

export function HeroSection() {
    const t = useTranslations('marketing.hero')
    const { language } = useLanguage()

    return (
        <>
            <HeroHeader />
            <main>
                <section className="before:bg-muted border-b border-border relative overflow-hidden before:absolute before:inset-1 before:rounded-2xl sm:before:inset-2 md:before:rounded-[2rem]">
                    {/* Striped Pattern Background */}
                    <div className="absolute inset-1 sm:inset-2 rounded-2xl md:rounded-[2rem] overflow-hidden">
                        <StripedPattern
                            direction="left"
                            className="text-foreground/[0.03] dark:text-foreground/[0.02]"
                        />
                    </div>
                    <div className="pt-20 pb-12 md:pt-32 md:pb-20 relative">
                        <div className="relative z-10 mx-auto max-w-7xl px-6 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">

                            {/* Text Content */}
                            <div className="flex-1 text-center lg:text-start max-w-2xl">
                                <Link
                                    href="#"
                                    className="group relative mx-auto lg:mx-0 flex w-fit items-center justify-center rounded-full px-4 py-1.5 mb-8 shadow-[inset_0_-8px_10px_#8fdfff1f] transition-shadow duration-500 ease-out hover:shadow-[inset_0_-5px_10px_#8fdfff3f] overflow-hidden"
                                >
                                    <span
                                        className={cn(
                                            "animate-gradient absolute inset-0 block h-full w-full rounded-[inherit] bg-gradient-to-r from-[#ffaa40]/50 via-[#9c40ff]/50 to-[#ffaa40]/50 bg-[length:300%_100%] p-[1px]"
                                        )}
                                        style={{
                                            WebkitMask:
                                                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                            WebkitMaskComposite: "destination-out",
                                            mask:
                                                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                            maskComposite: "subtract",
                                            WebkitClipPath: "padding-box",
                                        }}
                                    />
                                    <div
                                        aria-hidden
                                        className="relative flex size-6 items-center justify-center rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5"
                                    >
                                        <Sparkle className="size-3.5 fill-white stroke-white drop-shadow" />
                                    </div>
                                    <hr className="mx-2 h-4 w-px shrink-0 bg-neutral-500" />
                                    <AnimatedGradientText className="text-sm font-medium" colorFrom="hsl(var(--primary))" colorTo="#9c40ff">
                                        {t('badge')}
                                    </AnimatedGradientText>
                                    <ChevronRight className="ms-1 size-4 stroke-neutral-500 transition-transform duration-300 ease-in-out group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 rtl:rotate-180" />
                                    <BorderBeam size={40} duration={6} colorFrom="hsl(var(--primary))" colorTo="#9c40ff" />
                                </Link>

                                <h1 className="mt-4 text-balance text-4xl font-bold tracking-tight sm:text-5xl lg:text-7xl">
                                    <span className="text-primary">{t('headline.build')} {t('headline.scale')}</span>
                                    <br />
                                    <span>{t('headline.dominate')}</span>
                                </h1>

                                {/* Feature Subtitle with WordRotate */}
                                <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-x-2 text-lg sm:text-xl text-muted-foreground">
                                    <span>{t('headline.withFeature')}</span>
                                    <WordRotate
                                        words={t.raw('wordRotate') as string[]}
                                        duration={2500}
                                        className="text-primary font-semibold"
                                    />
                                </div>

                                <p className="text-muted-foreground mt-8 mb-10 text-balance text-lg sm:text-xl leading-relaxed">
                                    {t('description')}
                                </p>

                                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                                    <Button
                                        asChild
                                        size="lg"
                                        className="rounded-md h-12 px-8 text-base w-full sm:w-auto from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5"
                                    >
                                        <Link href={getAuthUrl(language, '/signup')} className="flex items-center gap-2">
                                            <span className="text-nowrap">{t('cta.startFree')}</span>
                                            <ArrowUpRight className="size-4" />
                                        </Link>
                                    </Button>
                                    <Button
                                        asChild
                                        size="lg"
                                        variant="outline"
                                        className="rounded-md h-12 px-8 text-base w-full sm:w-auto bg-background hover:bg-muted/50 dark:ring-input border-input/50 dark:border-input border-b-2 shadow-sm shadow-zinc-950/15 ring-1 ring-zinc-300"
                                    >
                                        <Link href="#features" className="flex items-center gap-2">
                                            <span className="text-nowrap">{t('cta.explore')}</span>
                                            <CirclePlay className="size-4" />
                                        </Link>
                                    </Button>
                                </div>
                            </div>

                            {/* Globe Visual */}
                            <div className="flex-1 relative">
                                <div
                                    className="relative aspect-square w-[280px] sm:w-[350px] md:w-[500px] lg:w-[600px] mx-auto"
                                >
                                    {/* Globe Component */}
                                    <Globe className="scale-[1.05]" />
                                    {/* Spinning Text - CSS responsive scaling */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" dir="ltr">
                                        <div className="scale-[0.47] sm:scale-[0.58] md:scale-[0.83] lg:scale-100 transition-none">
                                            <SpinningText
                                                radius={27}
                                                duration={25}
                                                className="font-bold text-foreground/80 text-sm tracking-widest"
                                                style={{ fontFamily: 'var(--font-jakarta), ui-sans-serif, system-ui, sans-serif' }}
                                            >
                                                EcoAI • EcoBuilder • EcoTheme • EcoChain • EcoApps • LiveOrders • SmartInvoice • EcoB2B •
                                            </SpinningText>
                                        </div>
                                    </div>
                                </div>
                                {/* Ambient Glow */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] bg-primary/20 blur-[100px] rounded-full pointer-events-none -z-10" />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    )
}
